import { useState } from "react";
import { DesktopFrame, MobileFrame } from "@/design-lab/Frames";
import { PremiumMeeting, PremiumJoin, PremiumAdmin } from "@/design-lab/premium";
import { BANNERS } from "@/design-lab/tokens";
import { Monitor, Smartphone } from "lucide-react";

const SCREENS = [
  { id: "meeting", label: "Meeting" },
  { id: "join", label: "Join" },
  { id: "admin", label: "Admin" },
];

const Pill = ({ active, children, onClick, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`px-4 py-2 rounded-full text-[12px] transition-colors duration-200 ${
      active
        ? "bg-emerald-500 text-black font-semibold"
        : "text-neutral-400 ring-1 ring-white/10 hover:text-white hover:bg-white/[0.06]"
    }`}
  >
    {children}
  </button>
);

export default function DesignLab() {
  const [screen, setScreen] = useState("meeting");
  const [role, setRole] = useState("host");
  const [mode, setMode] = useState("camera");
  const [openMenu, setOpenMenu] = useState("none");
  const [bannerId, setBannerId] = useState("brand");

  const render = (mobile) => {
    if (screen === "join") return <PremiumJoin mobile={mobile} bannerId={bannerId} />;
    if (screen === "admin") return <PremiumAdmin mobile={mobile} bannerId={bannerId} onBanner={setBannerId} />;
    return <PremiumMeeting mobile={mobile} role={role} mode={mode} openMenu={openMenu} />;
  };

  return (
    <div className="min-h-screen bg-[#0d0f0e] text-neutral-200" data-testid="design-lab">
      <header className="sticky top-0 z-40 bg-[#0d0f0e]/94 backdrop-blur-xl border-b border-white/[0.06]">
        <div className="max-w-[1600px] mx-auto px-6 py-5">
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-500/80">Udyog · design lab v2</div>
              <h1 className="font-display text-3xl tracking-tight text-white mt-1.5">Premium interface</h1>
              <p className="text-sm text-neutral-500 mt-1.5 max-w-2xl">
                Leave tucked away top-right · big labelled Chat dead-centre with two controls each side · circular
                controls only · edge-to-edge screen share · Udyog branding pinned top-left. One font family
                throughout.
              </p>
            </div>
            <div className="flex flex-col gap-2.5 items-end">
              <div className="flex items-center gap-2">
                {SCREENS.map((s) => (
                  <Pill
                    key={s.id}
                    active={screen === s.id}
                    onClick={() => setScreen(s.id)}
                    testid={`screen-tab-${s.id}`}
                  >
                    {s.label}
                  </Pill>
                ))}
              </div>
              {screen === "join" && (
                <div className="flex items-center gap-2">
                  {BANNERS.map((b) => (
                    <Pill
                      key={b.id}
                      active={bannerId === b.id}
                      onClick={() => setBannerId(b.id)}
                      testid={`banner-pill-${b.id}`}
                    >
                      {b.label}
                    </Pill>
                  ))}
                </div>
              )}
              {screen === "meeting" && (
                <div className="flex items-center gap-2">
                  <Pill active={role === "host"} onClick={() => setRole("host")} testid="role-host">
                    Host view
                  </Pill>
                  <Pill active={role === "student"} onClick={() => setRole("student")} testid="role-student">
                    Student view
                  </Pill>
                  <span className="w-px h-6 bg-white/10 mx-1" />
                  <Pill active={mode === "camera"} onClick={() => setMode("camera")} testid="mode-camera">
                    Camera
                  </Pill>
                  <Pill active={mode === "share"} onClick={() => setMode("share")} testid="mode-share">
                    Screen share
                  </Pill>
                  <span className="w-px h-6 bg-white/10 mx-1" />
                  <Pill active={openMenu === "none"} onClick={() => setOpenMenu("none")} testid="menu-none">
                    Menus closed
                  </Pill>
                  <Pill active={openMenu === "more"} onClick={() => setOpenMenu("more")} testid="menu-more">
                    More menu
                  </Pill>
                  {role === "host" && (
                    <Pill active={openMenu === "host"} onClick={() => setOpenMenu("host")} testid="menu-host">
                      Host menu
                    </Pill>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-6 py-8">
        <div className="flex items-center gap-3 mb-4">
          <span className="text-[11px] uppercase tracking-[0.24em] text-neutral-500">
            {screen} {screen === "meeting" ? `· ${role} · ${mode === "share" ? "presenting" : "camera"}` : ""}
          </span>
          <span className="h-px flex-1 bg-white/[0.06]" />
          <span className="flex items-center gap-1.5 text-[11px] text-neutral-600">
            <Monitor className="w-3.5 h-3.5" /> + <Smartphone className="w-3.5 h-3.5" /> side by side
          </span>
        </div>

        <div className="flex flex-col xl:flex-row gap-8 items-start" data-testid="preview-area">
          <DesktopFrame label={screen === "admin" ? "udyogclasses.com/admin" : "udyogclasses.com/b4918e7a"}>
            {render(false)}
          </DesktopFrame>
          <MobileFrame>{render(true)}</MobileFrame>
        </div>

        <div className="mt-10 p-5 rounded-2xl ring-1 ring-white/[0.07] bg-white/[0.02]">
          <div className="text-[11px] uppercase tracking-[0.24em] text-emerald-500/80 mb-2">Next step</div>
          <p className="text-sm text-neutral-400 leading-relaxed">
            Check the Host / Student toggle and the Screen share mode, then tell me what to tune (spacing, sizes,
            which two icons sit right of Chat). Once you approve, I&apos;ll apply this to the real meeting, join and
            admin screens. Your live app is still untouched.
          </p>
        </div>
      </main>
    </div>
  );
}
